import { WorkspaceIcon, type WorkspaceIconName } from '../icons';
import { fileExtension } from './upload-format';
import type { LibraryFeedItem } from './api';

/** Which family a file type belongs to: its label colour on the page icon. */
export type FileFamily = 'pdf' | 'word' | 'slides' | 'sheet' | 'other';

const FAMILIES: Readonly<Record<string, FileFamily>> = {
  PDF: 'pdf',
  DOCX: 'word',
  DOC: 'word',
  PPTX: 'slides',
  PPT: 'slides',
  XLSX: 'sheet',
  XLS: 'sheet',
  CSV: 'sheet',
  TSV: 'sheet',
};

export const fileFamily = (label: string): FileFamily => FAMILIES[label] ?? 'other';

const KIND_GLYPH: Partial<Record<LibraryFeedItem['kind'], WorkspaceIconName>> = {
  deck: 'slides',
  spreadsheet: 'table',
  table: 'table',
  chart: 'results',
  page: 'app',
};

/** The page outline, shared by every icon: a folded top-right corner on a 32 × 40 sheet. */
function Page({ dashed = false }: { dashed?: boolean }) {
  return (
    <svg className="ws-file-page-shape" viewBox="0 0 32 40" aria-hidden="true">
      <path
        d="M4 .75h18L31.25 10v26A3.25 3.25 0 0 1 28 39.25H4A3.25 3.25 0 0 1 .75 36V4A3.25 3.25 0 0 1 4 .75Z"
        strokeDasharray={dashed ? '3 2.5' : undefined}
      />
      {!dashed && <path className="ws-file-page-fold" d="M22 .75V7a3 3 0 0 0 3 3h6.25" />}
    </svg>
  );
}

/** A file: its type printed at the foot of the page ("PDF", "XLSX"), coloured by family. */
export function FilePageIcon({ name }: { name: string }) {
  const label = fileExtension(name);
  return (
    <span className="ws-file-page" data-family={fileFamily(label)} aria-hidden="true">
      <Page />
      {label && <span className="ws-file-page-type">{label}</span>}
    </span>
  );
}

/** Saved work that is not a file (a document, deck, sheet or chart): its kind as a glyph on the same page. */
export function KindPageIcon({ kind }: { kind: LibraryFeedItem['kind'] }) {
  return (
    <span className="ws-file-page" data-family="other" aria-hidden="true">
      <Page />
      <WorkspaceIcon
        name={KIND_GLYPH[kind] ?? 'document'}
        size={14}
        className="ws-file-page-glyph"
      />
    </span>
  );
}

/** The empty page waiting to be filled: a dashed outline with a "+" (the "Add files" row). */
export function AddPageIcon() {
  return (
    <span className="ws-file-page" data-variant="add" aria-hidden="true">
      <Page dashed />
      <WorkspaceIcon name="plus" size={14} className="ws-file-page-plus" />
    </span>
  );
}
